import type { Permission } from "@backchannel/tax-contracts";
import { permissionSchema } from "@backchannel/tax-contracts";
import { type ReactNode, createContext, useContext, useMemo } from "react";
import type { AppEnvConfig } from "~/config/env";
import { useAuth } from "~/lib/auth/auth-context";
import { onReplayNetworkFailure, replaySessionHeader } from "~/lib/replay";
import { type TaxApiClient, createTaxApiClient } from "./client";
import type { MockTransportOptions } from "./mock-transport";
import { type Transport, createHttpTransport } from "./transport";

const ApiContext = createContext<TaxApiClient | null>(null);

/**
 * Chooses the transport once, at the top of the tree. Screens never know which
 * one they got — that is the whole point of the seam, and it is why the same
 * tests cover mock demos and live deployments.
 */
export function ApiProvider({
  config,
  children,
  transport,
}: {
  config: AppEnvConfig;
  children: ReactNode;
  /** Test/story override. */
  transport?: Transport;
}) {
  const { getIdToken, signOut } = useAuth();

  const client = useMemo(() => {
    const resolved: Transport =
      transport ??
      (config.apiMode === "mock"
        ? createLazyMockTransport({
            latencyMs: import.meta.env.MODE === "test" ? 0 : 140,
            persistEvidenceTasks: true,
            persistFiscalTasks: true,
            // The env override is free-form text; anything that is not a real
            // permission is dropped rather than silently granting access.
            permissions: config.mockPermissions
              ? config.mockPermissions.filter(isPermission)
              : null,
          })
        : createHttpTransport({
            baseUrl: config.apiBaseUrl,
            environment: config.environment,
            getIdToken,
            onUnauthenticated: signOut,
            getReplaySessionId: replaySessionHeader,
            onNetworkFailure: onReplayNetworkFailure,
          }));

    return createTaxApiClient({ transport: resolved });
  }, [config, getIdToken, signOut, transport]);

  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

/**
 * Keep the demo dataset out of the live application's critical bundle. Mock
 * mode remains a faithful, first-class runtime, but its transport is loaded
 * only when the selected configuration actually needs it.
 */
function createLazyMockTransport(options: MockTransportOptions): Transport {
  let transportPromise: Promise<Transport> | null = null;

  return (request) => {
    transportPromise ??= import("./mock-transport").then(async ({ createMockTransport }) => {
      const mock = createMockTransport(options);
      // Dev builds can inject production-shaped failures (`?mockFail=504commit`);
      // the module is never imported in a production bundle.
      if (!import.meta.env.DEV) return mock;
      const { withMockFailures } = await import("./mock-failures");
      return withMockFailures(mock);
    });
    return transportPromise.then((transport) => transport(request));
  };
}

function isPermission(value: string): value is Permission {
  return permissionSchema.safeParse(value).success;
}

export function useApi(): TaxApiClient {
  const client = useContext(ApiContext);
  if (!client) throw new Error("useApi must be used inside <ApiProvider>");
  return client;
}
